import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";

const COLORS = ["#0f172a", "#00e5ff", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

const statusLabels = {
  draft: "Draft", under_review: "Under Review", pending_approval: "Pending Approval",
  approved: "Approved", active: "Active", expired: "Expired",
  terminated: "Terminated", archived: "Archived",
};

const tooltipStyle = { background: "white", border: "1px solid #e2e8f0", borderRadius: "4px", fontSize: "12px" };

function ChartLegend({ items, colorOffset = 0 }) {
  return (
    <div className="flex flex-wrap gap-3 mt-3 justify-center">
      {items.map((item, index) => (
        <div key={item.name} className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ background: COLORS[(index + colorOffset) % COLORS.length] }} />
          <span className="text-xs text-slate-600 dark:text-slate-400">{item.name}</span>
        </div>
      ))}
    </div>
  );
}

const EmptyState = ({ text }) => (
  <div className="h-[220px] flex items-center justify-center text-slate-400">{text}</div>
);

export function ContractCharts({ analytics }) {
  const statusChartData = useMemo(() =>
    analytics?.contracts_by_status
      ? Object.entries(analytics.contracts_by_status).map(([key, value]) => ({ name: statusLabels[key] || key, value }))
      : [],
    [analytics?.contracts_by_status]
  );

  const typeChartData = useMemo(() => analytics?.contracts_by_type || [], [analytics?.contracts_by_type]);

  const categoryChartData = useMemo(() =>
    (analytics?.contracts_by_category || []).map((c) => ({ name: c.category, value: c.count, total: c.value })),
    [analytics?.contracts_by_category]
  );

  const jurisdictionChartData = useMemo(() =>
    (analytics?.contracts_by_jurisdiction || []).map((j) => ({ name: j.jurisdiction, value: j.count })),
    [analytics?.contracts_by_jurisdiction]
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Status Distribution */}
      <Card className="chart-container" data-testid="status-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">Contracts by Status</CardTitle>
        </CardHeader>
        <CardContent>
          {statusChartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={statusChartData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2} dataKey="value">
                    {statusChartData.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
              <ChartLegend items={statusChartData} />
            </>
          ) : <EmptyState text="No data available" />}
        </CardContent>
      </Card>

      {/* Contracts by Type */}
      <Card className="chart-container" data-testid="type-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">Contracts by Type</CardTitle>
        </CardHeader>
        <CardContent>
          {typeChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={typeChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="type" tick={{ fontSize: 11 }} width={90} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#00e5ff" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No data available" />}
        </CardContent>
      </Card>

      {/* Category Distribution */}
      <Card className="chart-container" data-testid="category-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">Contracts by Category</CardTitle>
        </CardHeader>
        <CardContent>
          {categoryChartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={categoryChartData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {categoryChartData.map((_, index) => <Cell key={`cat-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(value) => [`${value} contract${value !== 1 ? "s" : ""}`, "Count"]} />
                </PieChart>
              </ResponsiveContainer>
              <ChartLegend items={categoryChartData} colorOffset={2} />
            </>
          ) : <EmptyState text="No category data" />}
        </CardContent>
      </Card>

      {/* Jurisdiction Distribution */}
      <Card className="chart-container" data-testid="jurisdiction-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">Contracts by Jurisdiction</CardTitle>
        </CardHeader>
        <CardContent>
          {jurisdictionChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={jurisdictionChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip contentStyle={tooltipStyle} formatter={(value) => [`${value} contract${value !== 1 ? "s" : ""}`, "Count"]} />
                <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No jurisdiction data" />}
        </CardContent>
      </Card>
    </div>
  );
}
